import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Phone, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { SITE, callLink, whatsappLink } from "../lib/constants";
import { SERVICES } from "../data/services";

export default function AppointmentDialog({ open, onOpenChange, defaultService = "" }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: defaultService,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid name and 10-digit phone number");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", form);
      setSuccess(true);
      toast.success("Appointment request received!", {
        description: "Our team will call you within 30 minutes.",
      });
      setTimeout(() => {
        setSuccess(false);
        setForm({ name: "", phone: "", email: "", service: defaultService, message: "" });
        onOpenChange(false);
      }, 2200);
    } catch (err) {
      toast.error("Could not submit. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden rounded-2xl border-slate-200"
        data-testid="appointment-dialog"
      >
        {success ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-teal-50 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-teal-600" />
            </div>
            <DialogTitle className="font-heading text-2xl font-medium text-slate-900">
              Booking received!
            </DialogTitle>
            <DialogDescription className="mt-2 text-slate-600">
              Our team will call you within 30 minutes to confirm your appointment.
            </DialogDescription>
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-teal-50 via-white to-white p-6 border-b border-slate-100">
              <DialogHeader className="text-left">
                <div className="text-[10px] uppercase tracking-[0.2em] text-teal-600 font-semibold mb-1">
                  Same-day appointment
                </div>
                <DialogTitle className="font-heading text-2xl font-medium text-slate-900">
                  Book Your Visit
                </DialogTitle>
                <DialogDescription className="text-slate-600 text-sm">
                  Tell us your name & phone. We will call you back to confirm a slot.
                </DialogDescription>
              </DialogHeader>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Label htmlFor="name" className="text-xs font-medium text-slate-700">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                  className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                  data-testid="appointment-name"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs font-medium text-slate-700">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  placeholder="e.g. +91 98765 43210"
                  className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                  data-testid="appointment-phone"
                />
              </div>
              <div>
                <Label htmlFor="service" className="text-xs font-medium text-slate-700">
                  Treatment (optional)
                </Label>
                <select
                  id="service"
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  data-testid="appointment-service"
                >
                  <option value="">Select treatment</option>
                  <option value="General Consultation">General Consultation</option>
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="message" className="text-xs font-medium text-slate-700">
                  Message (optional)
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Anything you'd like us to know?"
                  className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-teal-500 resize-none"
                  data-testid="appointment-message"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="appointment-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  "Confirm Appointment"
                )}
              </button>

              <div className="flex items-center gap-3 pt-2">
                <div className="flex-1 h-px bg-slate-200" />
                <div className="text-[10px] uppercase tracking-wider text-slate-400">or</div>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <a
                  href={callLink}
                  className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Phone className="w-4 h-4" /> Call
                </a>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] text-white text-sm font-medium hover:bg-[#1faa55]"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>

              <p className="text-[11px] text-slate-400 text-center pt-1">
                By submitting you agree to be contacted at the number provided.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
